import React, { useState, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TaskCard from './TaskCard';

const BoardColumn = ({
  column,
  cards = [],
  onCardClick,
  onCardMove,
  onCardReorder,
  onAddCard,
  onEditColumn,
  onDeleteColumn,
  members,
  canDragCard,
  canAddCard,
  canManageColumns
}) => {
  const [isEditing, setIsEditing] = useState(false);
  // Handle both 'title' and 'name' properties from backend
  const [columnTitle, setColumnTitle] = useState(column.title || column.name || 'Untitled');

  const [{ isOver }, drop] = useDrop({
    accept: 'card',
    drop: (item, monitor) => {
      // Only handle drop here if it wasn't already handled by a TaskCard
      const didDrop = monitor.didDrop();
      if (didDrop) return;

      if (item.columnId !== column.id) {
        console.log(`🎯 Dropped on empty column ${column.id}`);
        onCardMove(item.id, item.sourceColumnId, column.id, 0);
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const handleTitleSave = () => {
    const currentTitle = column.title || column.name || 'Untitled';
    if (columnTitle.trim() && columnTitle !== currentTitle) {
      onEditColumn(column.id, { title: columnTitle.trim(), name: columnTitle.trim() });
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setColumnTitle(column.title || column.name || 'Untitled');
      setIsEditing(false);
    }
  };

  // Get column styling based on column name/status - LIGHT THEME ONLY
  const getColumnConfig = useMemo(() => {
    const name = (column.title || column.name || '').toLowerCase();
    
    if (name.includes('done') || name.includes('complete') || name.includes('finished')) {
      return {
        headerBg: 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/40',
        accentColor: 'bg-emerald-500',
        countBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
        borderHover: 'border-emerald-400 dark:border-emerald-700',
        icon: 'CheckCircle',
        iconColor: 'text-emerald-500 dark:text-emerald-400'
      };
    }
    if (name.includes('review') || name.includes('testing') || name.includes('qa')) {
      return {
        headerBg: 'bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/40 dark:to-violet-950/40',
        accentColor: 'bg-purple-500',
        countBg: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
        borderHover: 'border-purple-400 dark:border-purple-700',
        icon: 'Eye',
        iconColor: 'text-purple-500 dark:text-purple-400'
      };
    }
    if (name.includes('progress') || name.includes('doing') || name.includes('active') || name.includes('working')) {
      return {
        headerBg: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40',
        accentColor: 'bg-blue-500',
        countBg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
        borderHover: 'border-blue-400 dark:border-blue-700',
        icon: 'Zap',
        iconColor: 'text-blue-500 dark:text-blue-400'
      };
    }
    if (name.includes('todo') || name.includes('backlog') || name.includes('new') || name.includes('to-do')) {
      return {
        headerBg: 'bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-900 dark:to-slate-800',
        accentColor: 'bg-slate-400',
        countBg: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
        borderHover: 'border-slate-400 dark:border-slate-700',
        icon: 'Circle',
        iconColor: 'text-slate-400 dark:text-slate-500'
      };
    }
    // Default
    return {
      headerBg: 'bg-gradient-to-r from-gray-50 to-slate-50 dark:from-slate-900 dark:to-slate-800',
      accentColor: 'bg-slate-400',
      countBg: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
      borderHover: 'border-slate-400 dark:border-slate-700',
      icon: 'Circle',
      iconColor: 'text-slate-400 dark:text-slate-500'
    };
  }, [column.title, column.name]);

  return (
    <div
      ref={drop}
      className={`
        flex flex-col w-80 min-w-80 bg-white/80 dark:bg-slate-800/70 backdrop-blur-md rounded-2xl 
        shadow-lg border-2 transition-all duration-300 ease-out
        ${isOver
          ? `${getColumnConfig.borderHover} shadow-[0_0_30px_rgba(99,102,241,0.15)] scale-[1.02]`
          : 'border-white/50 dark:border-slate-700/50 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600'
        }
      `}
    >
      {/* Top color accent bar */}
      <div className={`h-1 ${getColumnConfig.accentColor} rounded-t-2xl`} />

      {/* Enhanced Column Header */}
      <div className={`flex items-center justify-between p-4 ${getColumnConfig.headerBg} border-b border-slate-100 dark:border-slate-700`}>
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className={`flex-shrink-0 ${getColumnConfig.iconColor}`}>
            <Icon name={getColumnConfig.icon} size={18} />
          </div>
          
          {isEditing ? (
            <input
              type="text"
              value={columnTitle}
              onChange={(e) => setColumnTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleKeyPress}
              className="flex-1 px-3 py-1.5 text-sm font-semibold bg-white dark:bg-slate-700 dark:text-slate-100 border border-blue-300 dark:border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          ) : (
            <h3
              className={`text-sm font-bold text-slate-800 dark:text-slate-200 truncate transition-colors ${
                canManageColumns ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400' : 'cursor-default'
              }`}
              onClick={canManageColumns ? () => setIsEditing(true) : undefined}
              title={column.title || column.name || 'Untitled'}
            >
              {column.title || column.name || 'Untitled'}
            </h3>
          )}
          
          <span className={`flex-shrink-0 px-2.5 py-1 text-xs font-bold rounded-full min-w-[28px] text-center ${getColumnConfig.countBg}`}>
            {cards.length}
          </span>
        </div>
        
        <div className="flex items-center space-x-0.5 ml-2">
          {canAddCard && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAddCard(column.id)}
              className="h-7 w-7 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-lg"
              title="Add card"
            >
              <Icon name="Plus" size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Enhanced Cards Container */}
      <div 
        className="flex-1 p-3 space-y-3 overflow-y-auto custom-scrollbar" 
        style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '200px' }}
      >
        {cards.map((card) => (
          <TaskCard
            key={card.id}
            card={card}
            onClick={() => onCardClick(card)}
            onCardMove={onCardMove}
            onCardReorder={onCardReorder}
            members={members}
            canDrag={canDragCard ? canDragCard(card) : true}
          />
        ))}

        {cards.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className={`w-14 h-14 ${getColumnConfig.countBg} rounded-2xl flex items-center justify-center mb-4`}>
              <Icon name={getColumnConfig.icon} size={24} className={getColumnConfig.iconColor} />
            </div>
            <p className="text-sm font-medium text-slate-400 dark:text-slate-300 mb-1">No tasks yet</p>
            <p className="text-xs text-slate-300 dark:text-slate-500 mb-4">Drop a card here or create new</p>
            {canAddCard && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAddCard(column.id)}
                className="text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                <Icon name="Plus" size={14} className="mr-1.5" />
                Add card
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bottom padding for better scrolling */}
      <div className="h-2 rounded-b-2xl" />
    </div>
  );
};

export default BoardColumn;